<script>
  /**
   * Admin register of certified resellers.
   *
   * Two ways in, because there are two real situations: a reseller we sign from
   * scratch, and — much more common here — an existing customer that starts
   * reselling. The second is a single action rather than four, since doing it by
   * hand in the wrong order leaves an account that can log in and see nothing.
   */
  import { onMount } from 'svelte';
  import { push, link } from 'svelte-spa-router';
  import Layout from '../components/Layout.svelte';
  import { partnersApi, customersApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    Handshake, Plus, Loader, AlertCircle, CheckCircle, ChevronRight, ArrowUpRight, Trash2, X
  } from 'lucide-svelte';

  const STATUSES = ['prospect', 'certified', 'suspended', 'terminated'];

  let loading = true;
  let saving = false;
  let message = null;
  let partners = [];
  let customers = [];

  // Promote form
  let promoteCustomerId = '';
  let promoteCode = '';
  let promoteRole = 'admin';
  let promoteStatus = 'prospect';

  // Create form
  let form = { id: '', name: '', vatNumber: '', email: '', status: 'prospect' };

  let deletingId = null;

  // Only customers that can actually be promoted: one needs a portal account to
  // give the partner role to, and one already assigned to a reseller is refused.
  $: promotableCustomers = customers.filter((c) => c.hasPortalAccount && !c.partnerId);

  onMount(() => {
    load();
  });

  async function load() {
    loading = true;
    try {
      const [partnerResponse, customerResponse] = await Promise.all([
        partnersApi.list(),
        customersApi.getAll(),
      ]);
      partners = partnerResponse.partners || [];
      customers = customerResponse.customers || [];
    } catch (err) {
      console.error('[Partners] load failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function promote() {
    if (!promoteCustomerId) return;
    saving = true;
    message = null;
    try {
      const body = { customerId: promoteCustomerId, partnerRole: promoteRole, status: promoteStatus };
      if (promoteCode.trim()) body.id = promoteCode.trim().toUpperCase();
      const result = await partnersApi.promoteCustomer(body);
      message = { type: 'success', text: `${$_('partners.promoted')}: ${result.partner.id}` };
      promoteCustomerId = '';
      promoteCode = '';
      await load();
    } catch (err) {
      console.error('[Partners] promote failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function create() {
    if (!form.id.trim() || !form.name.trim()) return;
    saving = true;
    message = null;
    try {
      await partnersApi.create({
        id: form.id.trim().toUpperCase(),
        name: form.name.trim(),
        vatNumber: form.vatNumber.trim() || null,
        email: form.email.trim() || null,
        status: form.status,
      });
      message = { type: 'success', text: $_('partners.created') };
      form = { id: '', name: '', vatNumber: '', email: '', status: 'prospect' };
      await load();
    } catch (err) {
      console.error('[Partners] create failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      saving = false;
    }
  }

  async function remove(id) {
    try {
      await partnersApi.remove(id);
      message = { type: 'success', text: $_('partners.deleted') };
      deletingId = null;
      await load();
    } catch (err) {
      console.error('[Partners] delete failed:', err);
      message = { type: 'error', text: err.message };
    }
  }

  function statusClass(status) {
    switch (status) {
      case 'certified':
        return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300';
      case 'prospect':
        return 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300';
      case 'suspended':
        return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300';
      default:
        return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300';
    }
  }

  function customerLabel(customer) {
    return customer.companyName || `${customer.name || ''} ${customer.surname || ''}`.trim() || customer.id;
  }

  function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('it-IT', { dateStyle: 'medium' });
  }
</script>

<Layout>
  <div class="space-y-6">
    <div class="flex items-center space-x-4">
      <div class="p-3 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #00bfff, #0090cc);">
        <Handshake size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('partners.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('partners.subtitle')}</p>
      </div>
    </div>

    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}<CheckCircle size={20} />{:else}<AlertCircle size={20} />{/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => (message = null)}>&times;</button>
      </div>
    {/if}

    <!-- Promote an existing customer -->
    <div class="card p-6">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
        <ArrowUpRight size={20} class="text-primary-500" />
        <span>{$_('partners.promoteTitle')}</span>
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">{$_('partners.promoteHint')}</p>
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div class="md:col-span-2">
          <label class="label" for="promote-customer">{$_('partners.selectCustomer')}</label>
          <select id="promote-customer" class="input w-full" bind:value={promoteCustomerId}>
            <option value="">{$_('partners.selectCustomerPlaceholder')}</option>
            {#each promotableCustomers as customer (customer.id)}
              <option value={customer.id}>{customer.id} — {customerLabel(customer)}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="promote-code">{$_('partners.code')}</label>
          <input id="promote-code" type="text" class="input w-full" placeholder="—" bind:value={promoteCode} />
        </div>
        <div>
          <label class="label" for="promote-role">{$_('partners.role')}</label>
          <select id="promote-role" class="input w-full" bind:value={promoteRole}>
            <option value="admin">{$_('partners.roleAdmin')}</option>
            <option value="sales">{$_('partners.roleSales')}</option>
          </select>
        </div>
        <div>
          <label class="label" for="promote-status">{$_('partners.status')}</label>
          <select id="promote-status" class="input w-full" bind:value={promoteStatus}>
            {#each STATUSES as status}
              <option value={status}>{$_(`partners.statuses.${status}`)}</option>
            {/each}
          </select>
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <p class="text-xs text-gray-400">{$_('partners.onlyWithAccount')}</p>
        <button class="btn btn-primary flex items-center space-x-2" onclick={promote} disabled={saving || !promoteCustomerId}>
          {#if saving}<Loader size={18} class="animate-spin" />{:else}<ArrowUpRight size={18} />{/if}
          <span>{$_('partners.promoteButton')}</span>
        </button>
      </div>
    </div>

    <!-- Create from scratch -->
    <div class="card p-6">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
        <Plus size={20} class="text-primary-500" />
        <span>{$_('partners.createTitle')}</span>
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label class="label" for="new-code">{$_('partners.code')}</label>
          <input id="new-code" type="text" class="input w-full" bind:value={form.id} />
        </div>
        <div class="md:col-span-2">
          <label class="label" for="new-name">{$_('partners.name')}</label>
          <input id="new-name" type="text" class="input w-full" bind:value={form.name} />
        </div>
        <div>
          <label class="label" for="new-vat">{$_('partners.vatNumber')}</label>
          <input id="new-vat" type="text" class="input w-full" bind:value={form.vatNumber} />
        </div>
        <div>
          <label class="label" for="new-email">{$_('partners.email')}</label>
          <input id="new-email" type="email" class="input w-full" bind:value={form.email} />
        </div>
      </div>
      <div class="flex items-center justify-between mt-4">
        <p class="text-xs text-gray-400">{$_('partners.codeHint')}</p>
        <button class="btn btn-secondary flex items-center space-x-2" onclick={create} disabled={saving || !form.id.trim() || !form.name.trim()}>
          <Plus size={18} />
          <span>{$_('partners.createButton')}</span>
        </button>
      </div>
    </div>

    <!-- Register -->
    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if partners.length === 0}
      <div class="card p-12 text-center">
        <Handshake size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('partners.noPartners')}</p>
      </div>
    {:else}
      <div class="card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.code')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.name')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.status')}</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.certifiedUntil')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.customersCount')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.usersCount')}</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              {#each partners as partner (partner.id)}
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="px-4 py-4 font-mono text-sm text-gray-900 dark:text-white">{partner.id}</td>
                  <td class="px-4 py-4 font-medium text-gray-900 dark:text-white">{partner.name}</td>
                  <td class="px-4 py-4">
                    <span class="px-2 py-0.5 text-xs font-bold border rounded {statusClass(partner.status)}">
                      {$_(`partners.statuses.${partner.status}`)}
                    </span>
                    {#if partner.status === 'certified' && !partner.certified}
                      <span class="ml-1 px-2 py-0.5 text-xs font-bold border rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300">
                        {$_('partners.certifiedNo')}
                      </span>
                    {/if}
                  </td>
                  <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(partner.certifiedUntil)}</td>
                  <td class="px-4 py-4 text-sm text-right text-gray-600 dark:text-gray-300">{partner.customerCount ?? 0}</td>
                  <td class="px-4 py-4 text-sm text-right text-gray-600 dark:text-gray-300">{partner.userCount ?? 0}</td>
                  <td class="px-4 py-4 text-right">
                    {#if deletingId === partner.id}
                      <div class="flex items-center justify-end space-x-1">
                        <span class="text-xs text-gray-500 mr-2">{$_('partners.deleteConfirm')}</span>
                        <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg text-xs font-medium" onclick={() => remove(partner.id)}>{$_('common.confirm')}</button>
                        <button class="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" onclick={() => (deletingId = null)}><X size={16} /></button>
                      </div>
                    {:else}
                      <div class="flex items-center justify-end space-x-1">
                        <a href="/partners/{partner.id}" use:link class="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg inline-flex">
                          <ChevronRight size={18} />
                        </a>
                        <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => (deletingId = partner.id)}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>
</Layout>
